import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { breakpoints } from '@/styles/util';

import { fontStyles } from '../../styles/typography';
import { AvatarRadius } from '../Avatars/Avatar';
import { BottomPanelModal } from '../Modal/BottomPanelModal';
import styles from './multiselect.module.css';
import { MultiselectContent, MultiselectContentProps } from './MultiselectContent';
import { MultiselectTrigger, MultiselectTriggerProps } from './MultiselectTrigger';

export type MultiselectItem = {
  /**
   * The label to be displayed
   */
  label: string;
  /**
   * Icon of the item
   */
  icon?: string;
  /**
   * Alternate text for the icon
   */
  iconAlt?: string;
  /**
   * Radius of the icon.
   * @default '100%'
   */
  iconRadius?: AvatarRadius;
  /**
   * The underlying value of the item
   */
  value: string;
};

type MultiselectProps = Pick<
  MultiselectContentProps,
  'title' | 'showSelectAllButton' | 'itemsLayout' | 'search' | 'isLoading'
> &
  Pick<MultiselectTriggerProps, 'triggerVariant' | 'label'> & {
    /**
     * The items to display in the multiselect.
     * Each item should have a label and a value.
     * The value is used to identify the item.
     * The label is what is displayed to the user.
     */
    items: MultiselectItem[];
    /**
     * Callback that is called when the selected items change
     *
     * @param selected - The currently selected items represented by their values
     */
    onChange?: (selected: string[]) => void;
    /**
     * The items that should be selected.
     * Use this prop if you want to control the state of the component.
     */
    value?: string[];
    /**
     * Initial value of the input, also used to determine whether a selected state should be displayed or not.
     */
    defaultValue?: string[];
    /**
     * Callback that is called when the dropdown is closed
     */
    onDropdownClose?: () => void;
    /**
     * Shows a modal on mobile when set to true. This also removes the default max-height
     * on the items container as this variant is used when having many options.
     */
    modal?: boolean;
    /**
     * Allows multiple option selection when set to true.
     */
    multiselect?: boolean;
  };

export type DismissalMode = 'pointer' | 'escape';

export const Multiselect = ({
  items,
  onChange: onChangeProp,
  value: valueProp,
  defaultValue,
  onDropdownClose,
  title,
  showSelectAllButton,
  modal,
  itemsLayout = 'default',
  triggerVariant = 'default',
  label,
  search,
  isLoading,
  multiselect,
}: MultiselectProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(valueProp ?? defaultValue ?? []);

  const dismissalMode = useRef<DismissalMode | null>(null);
  const isMobile = useMedia(breakpoints.mobile, false);
  const showModalVariant = isMobile && modal;

  const handleDismissalModeChange = (mode: DismissalMode) => {
    dismissalMode.current = mode;
  };

  const handleCloseAutoFocus = (event: Event) => {
    if (dismissalMode.current === 'pointer') {
      event.preventDefault();
    }

    dismissalMode.current = null;
  };

  const handleValueChange = (newValue: string[]) => {
    setValue(newValue);
    onChangeProp?.(newValue);

    // Close modal after single selection on mobile
    if (!multiselect && showModalVariant) {
      handleOpenChange(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onDropdownClose?.();
    }

    setOpen(newOpen);
  };

  // Update internal value on value property change
  useEffect(() => setValue(valueProp ?? []), [valueProp]);

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <MultiselectTrigger
        items={items}
        value={value}
        defaultValue={defaultValue}
        toggleOpenState={() => setOpen(!open)}
        triggerVariant={triggerVariant}
        label={label}
      />
      {!showModalVariant && (
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            onCloseAutoFocus={handleCloseAutoFocus}
            onEscapeKeyDown={() => handleDismissalModeChange('escape')}
            onInteractOutside={() => handleDismissalModeChange('pointer')}
            align="start"
            className={fontStyles({
              variant: 'BODY_MD',
              class: [
                'bg-special-background-modal shadow-25 p-2 rounded-lg w-[dropdownDesktop]',
                'mt-2 z-90 select-none overflow-y-auto',
                modal ? 'max-h-auto' : 'max-h-[min(50dvh,300px)]',
                styles['popover-animations'],
              ],
            })}
            side="bottom"
            role="listbox"
            aria-multiselectable={true}
          >
            <MultiselectContent
              title={title}
              showSelectAllButton={showSelectAllButton}
              value={value}
              items={items}
              onChange={handleValueChange}
              itemsLayout={itemsLayout}
              search={search}
              isLoading={isLoading}
              multiselect={multiselect}
              onDismissalModeChange={handleDismissalModeChange}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
      {showModalVariant && (
        <BottomPanelModal
          isOpen={open}
          onOpenChange={handleOpenChange}
          onClose={() => handleOpenChange(false)}
        >
          <MultiselectContent
            title={title}
            showSelectAllButton={showSelectAllButton}
            value={value}
            items={items}
            onChange={handleValueChange}
            modalVariant={true}
            itemsLayout={itemsLayout}
            search={search}
            isLoading={isLoading}
            multiselect={multiselect}
            onDismissalModeChange={handleDismissalModeChange}
          />
        </BottomPanelModal>
      )}
    </DropdownMenu.Root>
  );
};
